import type {CSS} from '@pinecast/styles';
import styled from '@pinecast/styles';
import * as React from 'react';

import {DEFAULT_FONT} from './constants';
import Radio from './Radio';

const NativeButton = styled(
  'button',
  ({$selected}: {$selected: boolean}, designColors) => {
    const UNSELECTED_SHADOW =
      '0 0 0 0 rgba(0, 0, 0, 0), 0 0 0 rgba(0, 0, 0, 0)';

    const focusColor = designColors.accentBlueLight;
    const selectedShadow = `0 0 0 4px ${designColors.accentBlueLight}, 0 0 0 rgba(0, 0, 0, 0)`;
    return {
      backgroundColor: designColors.contentBackground,
      border: `1px solid ${
        $selected
          ? designColors.borderBlueNormal
          : designColors.contentBackground
      }`,
      borderRadius: 4,
      boxShadow: $selected
        ? `${selectedShadow}, 0 0 0 0 ${focusColor}`
        : `${UNSELECTED_SHADOW}, 0 0 0 0 ${focusColor}`,
      color: $selected
        ? designColors.contentText
        : designColors.contentTextAccent,
      cursor: 'pointer',
      display: 'flex',
      flexDirection: 'column',
      fontFamily: DEFAULT_FONT,
      fontSize: 16,
      fontWeight: 400,
      padding: '8px 12px',
      position: 'relative',
      textAlign: 'left',
      transition: 'border 0.2s, box-shadow 0.2s, color 0.2s, opacity 0.2s',
      userSelect: 'none',

      ':hover:not(:focus):not(:active)': {
        textShadow: $selected ? undefined : '0 2px 2px rgba(0, 0, 0, 0.15)',
      },
      ':focus': {
        border: `1px solid ${
          $selected ? designColors.accentBlue : designColors.contentBackground
        }`,
        boxShadow: $selected
          ? `${selectedShadow}, 0 0 0 3px ${focusColor}`
          : `${UNSELECTED_SHADOW}, 0 0 0 3px ${focusColor}`,
        outline: 'none',
      },
    };
  },
  {className: 'Button-nativeButton'},
);

type Props = {
  children: React.ReactNode;
  className?: string;
  onClick: () => void;
  selected: boolean;
  style?: CSS;
  tabIndex?: number;
  title?: string;
};

export default class OptionButton extends React.PureComponent<Props> {
  handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    this.props.onClick();
  };

  render() {
    const {children, className, selected, style, tabIndex, title} = this.props;

    return (
      <NativeButton
        aria-checked={selected}
        role="radio"
        $selected={selected}
        className={className}
        onClick={this.handleClick}
        style={style}
        tabIndex={tabIndex}
        title={title}
        type="button"
      >
        {children}
      </NativeButton>
    );
  }
}

export const OptionButtonWithRadio = ({
  alignInput,
  children,
  disabled,
  name,
  ...rest
}: OptionButton['props'] & {
  alignInput?: 'top' | 'center';
  disabled?: boolean;
  name: string;
}) => {
  return (
    <OptionButton {...rest}>
      <Radio
        alignInput={alignInput}
        checked={rest.selected}
        disabled={disabled}
        hideFromScreenReaders
        name={name}
        onChange={selected => {
          if (selected) {
            rest.onClick();
          }
        }}
        style={{padding: 0}}
        tabIndex={-1}
        text={children}
      />
    </OptionButton>
  );
};
